/**
 * Modal - Accessible modal component with focus trap and escape key handling
 *
 * Features:
 * - Focus is trapped inside modal when open
 * - Escape key closes the modal
 * - Click outside closes the modal (optional)
 * - Returns focus to trigger element on close
 */

import { useEffect, useCallback, type ReactNode } from 'react';
import FocusTrap from 'focus-trap-react';

interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Modal content */
  children: ReactNode;
  /** Whether clicking the backdrop closes the modal (default: true) */
  closeOnBackdropClick?: boolean;
  /** Additional class names for the backdrop */
  backdropClassName?: string;
  /** Additional class names for the modal container */
  containerClassName?: string;
}

export function Modal({
  isOpen,
  onClose,
  children,
  closeOnBackdropClick = true,
  backdropClassName = '',
  containerClassName = '',
}: ModalProps) {
  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: true,
        onDeactivate: onClose,
      }}
    >
      <div
        className={`fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 ${backdropClassName}`}
        onClick={closeOnBackdropClick ? onClose : undefined}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={containerClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </FocusTrap>
  );
}

/**
 * ModalHeader - Standard header for modals
 */
interface ModalHeaderProps {
  title: string;
  subtitle?: string;
  onClose?: () => void;
}

export function ModalHeader({ title, subtitle, onClose }: ModalHeaderProps) {
  return (
    <div className="bg-forest-800 text-white px-6 py-4 flex items-center justify-between">
      <div>
        <h2 className="text-lg font-display font-semibold">{title}</h2>
        {subtitle && <p className="text-forest-200 text-sm">{subtitle}</p>}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="p-1 hover:bg-forest-700 rounded-lg transition-colors"
          aria-label="Close modal"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * ModalBody - Standard body/content area for modals
 */
interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export function ModalBody({ children, className = '' }: ModalBodyProps) {
  return (
    <div className={`p-6 ${className}`}>
      {children}
    </div>
  );
}

/**
 * ModalFooter - Standard footer for modals with action buttons
 */
interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export function ModalFooter({ children, className = '' }: ModalFooterProps) {
  return (
    <div className={`border-t border-sage-200 px-6 py-4 flex justify-end gap-3 ${className}`}>
      {children}
    </div>
  );
}
